/**
 * The GateCheckPro authorization vocabulary (SDAD Sections 12.3, 51).
 *
 * Two separate authority spaces meet here and must never be conflated:
 *
 *   - *Organization capabilities* are held inside exactly one organizer tenant.
 *     A role never escapes the organization that granted it.
 *   - *Platform capabilities* belong to CABS operations staff. There is
 *     deliberately no capability that means "see everything": an onboarding
 *     reviewer has no route to organizer finance, and a support agent has no
 *     route to security audit (SDAD Section 9.5).
 *
 * Both are declared here rather than in the API alone, because the human
 * surfaces must render exactly the capabilities the server will honour. A menu
 * item the server would refuse is a product lie.
 */
/** Capabilities scoped to a single organizer tenant. */
export const ORG_CAPABILITIES = [
    'ORG_VIEW',
    'ORG_PROFILE_MANAGE',
    'ORG_MEMBER_MANAGE',
    'ORG_ROLE_MANAGE',
    'ORG_VERIFICATION_SUBMIT',
    // Declared now, honoured by their own slices. Declaring them early keeps the
    // role table stable; granting them does not conjure the feature into being,
    // because no route consumes them yet.
    'EVENT_MANAGE',
    'TICKETING_MANAGE',
    'GATE_OPERATE',
    'GATE_SUPERVISE',
    'FINANCE_VIEW',
    'FINANCE_APPROVE',
    'SUPPORT_ACT',
];
/** Roles a member may hold inside an organization (SDAD Section 12.3). */
export const ORG_ROLES = [
    'ORG_OWNER',
    'ORG_ADMIN',
    'EVENT_MANAGER',
    'TICKETING_MANAGER',
    'GATE_SUPERVISOR',
    'SCANNER',
    'FINANCE_VIEWER',
    'FINANCE_APPROVER',
    'SUPPORT',
];
/**
 * Role to capability expansion.
 *
 * `SCANNER` is the sharpest illustration of least privilege (SDAD Section 25.1):
 * it does not even carry `ORG_VIEW`. A scanner device's operator has no business
 * reading the organizer workspace, and a stolen scanner therefore yields no
 * organizer data beyond the gate it was provisioned for.
 */
export const ROLE_CAPABILITIES = Object.freeze({
    ORG_OWNER: [
        'ORG_VIEW',
        'ORG_PROFILE_MANAGE',
        'ORG_MEMBER_MANAGE',
        'ORG_ROLE_MANAGE',
        'ORG_VERIFICATION_SUBMIT',
        'EVENT_MANAGE',
        'TICKETING_MANAGE',
        'GATE_SUPERVISE',
        'GATE_OPERATE',
        'FINANCE_VIEW',
        'FINANCE_APPROVE',
        'SUPPORT_ACT',
    ],
    ORG_ADMIN: [
        'ORG_VIEW',
        'ORG_PROFILE_MANAGE',
        'ORG_MEMBER_MANAGE',
        'ORG_ROLE_MANAGE',
        'ORG_VERIFICATION_SUBMIT',
        'EVENT_MANAGE',
        'TICKETING_MANAGE',
        'GATE_SUPERVISE',
        'SUPPORT_ACT',
    ],
    EVENT_MANAGER: ['ORG_VIEW', 'EVENT_MANAGE'],
    TICKETING_MANAGER: ['ORG_VIEW', 'EVENT_MANAGE', 'TICKETING_MANAGE'],
    GATE_SUPERVISOR: ['ORG_VIEW', 'GATE_SUPERVISE', 'GATE_OPERATE'],
    SCANNER: ['GATE_OPERATE'],
    FINANCE_VIEWER: ['ORG_VIEW', 'FINANCE_VIEW'],
    FINANCE_APPROVER: ['ORG_VIEW', 'FINANCE_VIEW', 'FINANCE_APPROVE'],
    SUPPORT: ['ORG_VIEW', 'SUPPORT_ACT'],
});
export function capabilitiesForRoles(roles) {
    const set = new Set();
    for (const role of roles) {
        for (const capability of ROLE_CAPABILITIES[role] ?? [])
            set.add(capability);
    }
    return ORG_CAPABILITIES.filter((capability) => set.has(capability));
}
/**
 * CABS operations capabilities (SDAD Section 9.5).
 *
 * `OPS_PLATFORM_ADMIN` grants exactly one power: administering these grants. It
 * is not a skeleton key, and it deliberately does not imply organizer data
 * access.
 */
export const PLATFORM_CAPABILITIES = [
    'OPS_ORGANIZER_ONBOARDING',
    'OPS_SUPPORT',
    'OPS_TRUST_SAFETY',
    'OPS_FINANCE_RECONCILIATION',
    'OPS_SECURITY',
    'OPS_PLATFORM_ADMIN',
];
/**
 * Capabilities that may only be exercised from a session that has satisfied a
 * second factor (SDAD Section 12.2).
 *
 * The rule is enforced at the capability, not at the route. A future route that
 * consumes `FINANCE_APPROVE` inherits the requirement without anyone having to
 * remember it.
 */
export const MFA_REQUIRED_ORG_CAPABILITIES = Object.freeze([
    'ORG_MEMBER_MANAGE',
    'ORG_ROLE_MANAGE',
    'GATE_SUPERVISE',
    'FINANCE_VIEW',
    'FINANCE_APPROVE',
]);
/** Every CABS privileged capability requires a second factor. Without exception. */
export const MFA_REQUIRED_PLATFORM_CAPABILITIES = Object.freeze(PLATFORM_CAPABILITIES);
export function orgCapabilityRequiresMfa(capability) {
    return MFA_REQUIRED_ORG_CAPABILITIES.includes(capability);
}
export function platformCapabilityRequiresMfa(capability) {
    return MFA_REQUIRED_PLATFORM_CAPABILITIES.includes(capability);
}
/**
 * Human-readable labels for the surfaces. Kept beside the capability list so a
 * new capability cannot ship with a raw enum name leaking into the UI.
 */
export const ORG_ROLE_LABELS = Object.freeze({
    ORG_OWNER: 'Owner',
    ORG_ADMIN: 'Administrator',
    EVENT_MANAGER: 'Event manager',
    TICKETING_MANAGER: 'Ticketing manager',
    GATE_SUPERVISOR: 'Gate supervisor',
    SCANNER: 'Scanner operator',
    FINANCE_VIEWER: 'Finance viewer',
    FINANCE_APPROVER: 'Finance approver',
    SUPPORT: 'Support',
});
export const ORG_ROLE_DESCRIPTIONS = Object.freeze({
    ORG_OWNER: 'Full control of the organization, including roles and finance.',
    ORG_ADMIN: 'Runs the organization day to day. No finance approval.',
    EVENT_MANAGER: 'Creates and configures events.',
    TICKETING_MANAGER: 'Configures ticket products, capacity and sale windows.',
    GATE_SUPERVISOR: 'Runs the gate, resolves exceptions and authorises overrides.',
    SCANNER: 'Operates a scanner at an assigned gate. No workspace access.',
    FINANCE_VIEWER: 'Reads settlement and payout state.',
    FINANCE_APPROVER: 'Reads and approves settlement and payout actions.',
    SUPPORT: 'Assists attendees with orders and tickets.',
});
export const PLATFORM_CAPABILITY_LABELS = Object.freeze({
    OPS_ORGANIZER_ONBOARDING: 'Organizer onboarding',
    OPS_SUPPORT: 'Ticketing support',
    OPS_TRUST_SAFETY: 'Trust and safety',
    OPS_FINANCE_RECONCILIATION: 'Finance and reconciliation',
    OPS_SECURITY: 'Security operations',
    OPS_PLATFORM_ADMIN: 'Platform administration',
});
/**
 * What an actor can do *right now*, as opposed to what they hold.
 *
 * The two differ whenever a privileged capability is held by an account whose
 * current session has not proved a second factor. The server enforces the
 * distinction; surfaces must render it, or they offer buttons that will be
 * refused — which trains people to treat refusals as bugs.
 *
 * Kept here rather than in each surface so the rule cannot drift: a capability
 * added to `MFA_REQUIRED_ORG_CAPABILITIES` becomes correctly gated in every
 * product at once.
 */
export function usableOrgCapabilities(held, session) {
    if (session.mfaSatisfied)
        return [...held];
    return held.filter((capability) => !orgCapabilityRequiresMfa(capability));
}
export function usablePlatformCapabilities(held, session) {
    if (session.mfaSatisfied)
        return [...held];
    return held.filter((capability) => !platformCapabilityRequiresMfa(capability));
}
//# sourceMappingURL=authorization.js.map